import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import ChartLoader from "../../../Shared/Loader";

const CandlestickChart = ({ inputData,isLoading }) => {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  const [width, setWidth] = useState(0);
  const height = 360;

  // Prepare data safely
  const data = inputData?.date?.map((d, i) => ({
    date: new Date(d),
    low: inputData.min[i],
    high: inputData.max[i],
    open: inputData.q1[i],
    close: inputData.q3[i],
    mean: inputData.mean[i],
    std: inputData.std[i]
  }));

  // Observe parent width (AUTO WIDTH)
  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setWidth(width);
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  // D3 chart render
  useEffect(() => {
    if (!data || !data.length || !width) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const tooltip = d3.select(tooltipRef.current);

    const margin = { top: 20, right: 30, bottom: 70, left: 60 };

    const x = d3
      .scaleBand()
      .domain(data.map(d => d.date))
      .range([margin.left, width - margin.right])
      .padding(0.55);

    const y = d3
      .scaleLinear()
      .domain([
        d3.min(data, d => d.low) - 1,
        d3.max(data, d => d.high) + 1
      ])
      .nice()
      .range([height - margin.bottom, margin.top]);

    // X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickFormat(d3.timeFormat("%b %d")))
      .selectAll("text")
      .attr("transform", "rotate(-30)")
      .style("text-anchor", "end");

    // Y Axis
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y));

    // Mean line
    const meanLine = d3
      .line()
      .x(d => x(d.date) + x.bandwidth() / 2)
      .y(d => y(d.mean));

    svg
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#a5a9b0ff")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "5 4")
      .attr("d", meanLine);

    // Mean points
    svg
      .selectAll(".mean-point")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", d => x(d.date) + x.bandwidth() / 2)
      .attr("cy", d => y(d.mean))
      .attr("r", 3)
      .attr("fill", "#2563eb");

    // X Axis title
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", height - 15)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("fill", "#5c5b5b")
      .text("Date");

    // Y Axis title
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -(height / 2))
      .attr("y", 15)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("fill", "#5c5b5b")
      .text("Temperature");

    // Candles
    const candles = svg
      .selectAll(".candle")
      .data(data)
      .enter()
      .append("g")
      .style("cursor", "pointer");

    candles.each(function (d, i) {
      const prevClose = i === 0 ? d.close : data[i - 1].close;
      const isUp = d.close >= prevClose;
      const color = isUp ? "#16a34a" : "#dc2626";
      const midlineColor = isUp ? "#23894aff" : "#be2121ff";
      const g = d3.select(this);

      // Wick
      g.append("line")
        .attr("x1", x(d.date) + x.bandwidth() / 2)
        .attr("x2", x(d.date) + x.bandwidth() / 2)
        .attr("y1", y(d.low))
        .attr("y2", y(d.high))
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .on("mouseover", e => showTooltip(e, d, isUp))
        .on("mousemove", moveTooltip)
        .on("mouseout", hideTooltip);


      // Body
      g.append("rect")
        .attr("x", x(d.date))
        .attr("y", y(Math.max(d.open, d.close)))
        .attr("width", x.bandwidth())
        .attr("height", Math.abs(y(d.open) - y(d.close)))
        .attr("fill", color)
        .on("mouseover", e => showTooltip(e, d, isUp))
        .on("mousemove", moveTooltip)
        .on("mouseout", hideTooltip);

      // Middle (mean) line inside candle
      g.append("line")
        .attr("x1", x(d.date))
        .attr("x2", x(d.date) + x.bandwidth())
        .attr("y1", y(d.mean))
        .attr("y2", y(d.mean))
        .attr("stroke", midlineColor)
        .attr("stroke-width",2);
    });

    function showTooltip(event, d, isUp) {
      tooltip
        .style("opacity", 1)
        .html(`
          <strong>${d3.timeFormat("%Y-%m-%d")(d.date)}</strong><br/>
          Min: ${d.low}<br/>
          Max: ${d.high}<br/>
          Q1: ${d.open}<br/>
          Q3: ${d.close}<br/>
          Mean: ${d.mean}<br/>
          Std: ${d.std}<br/>
        `);
      moveTooltip(event);
    }

    function moveTooltip(event) {
      const bounds = wrapperRef.current.getBoundingClientRect();
      tooltip
        .style("left", event.clientX - bounds.left + 12 + "px")
        .style("top", event.clientY - bounds.top - 30 + "px");
    }

    function hideTooltip() {
      tooltip.style("opacity", 0);
    }
  }, [data, width]);

  return (
    <div ref={wrapperRef} style={{ width: "100%", position: "relative", overflow: "hidden" }}>
      {
        isLoading ?
          <div style={{ height: height }} className="d-flex justify-content-center align-items-center">
            <ChartLoader zIndex={1}/>
          </div>
          :
          data?.length ?
            <>
              <svg
                ref={svgRef}
                width="100%"
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
              />
              <div
                ref={tooltipRef}
                style={{
                  position: "absolute",
                  background: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  fontSize: "12px",
                  pointerEvents: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  opacity: 0
                }}
              />
            </>
            :
            <div style={{ height: height }} className="d-flex justify-content-center align-items-center">
              <h4>Data not available!</h4>
            </div>
      }
    </div>
  );
};

export default CandlestickChart;
